"use client";

import { useEffect, useState } from "react";

const isFirestoreAssertion = (msg: string) =>
  msg.includes("FIRESTORE") && msg.includes("INTERNAL ASSERTION FAILED");

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [countdown, setCountdown] = useState(3);
  const firestoreCrash = isFirestoreAssertion(error?.message || "");

  useEffect(() => {
    console.error("Global error caught:", error);
    // Errors caught by React error boundaries NEVER hit window.onerror, so
    // without this beacon they are invisible in /api/diag — a device can
    // crash-loop through this screen while telemetry shows clean boots.
    try {
      (window as any).__bbSend?.("global-error", {
        message: String(error?.message || "").slice(0, 400),
        digest: error?.digest || "",
        stack: error?.stack ? String(error.stack).slice(0, 1500) : "",
      });
    } catch { /* ignore */ }
  }, [error]);

  // Firestore internal assertion errors can't be recovered with reset() —
  // the SDK's internal state is corrupt and only a full reload fixes it.
  // ⚠️ Loop-guarded: if the assertion recurs on every fresh boot (seen on
  // iOS/WebKit with corrupted IndexedDB), an unguarded auto-reload here
  // loops forever until iOS kills the app ("A problem repeatedly
  // occurred"). Max 2 auto-reloads per minute; when attempts can't be
  // tracked, never auto-reload — the manual button remains.
  useEffect(() => {
    if (!firestoreCrash) return;
    let allowed = false;
    try {
      const KEY = "bb-ge-reload";
      const now = Date.now();
      const prev = JSON.parse(sessionStorage.getItem(KEY) || '{"n":0,"t":0}');
      const within = now - (prev.t || 0) < 60000;
      const n = within ? (prev.n || 0) + 1 : 1;
      sessionStorage.setItem(KEY, JSON.stringify({ n, t: now }));
      allowed = n <= 2;
    } catch { allowed = false; }
    if (!allowed) return;
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          try { (window as any).__bbSend?.("reload-globalerror", {}); } catch { /* ignore */ }
          window.location.reload();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [firestoreCrash]);

  return (
    <html>
      <body
        style={{
          background: "#080c0a",
          color: "#e8e8e8",
          fontFamily: "Inter, system-ui, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          margin: 0,
          padding: "2rem",
          boxSizing: "border-box",
        }}
      >
        <div style={{ maxWidth: "480px", width: "100%", textAlign: "center" }}>
          {firestoreCrash ? (
            <>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔄</div>
              <h2 style={{ color: "#fff", marginBottom: "0.5rem", fontSize: "1.4rem" }}>
                Connection hiccup
              </h2>
              <p style={{ color: "#999", marginBottom: "1.5rem", lineHeight: 1.5 }}>
                A database sync error occurred. Reloading automatically…
              </p>
              <div
                style={{
                  background: "#1a1a1a",
                  borderRadius: "8px",
                  padding: "1rem",
                  marginBottom: "1.5rem",
                  color: "#666",
                  fontSize: "0.85rem",
                }}
              >
                Reloading in {countdown}…
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "0.6rem 1.5rem",
                  background: "#22c55e",
                  color: "#000",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                Reload Now
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>
              <h2 style={{ color: "#fff", marginBottom: "0.5rem", fontSize: "1.4rem" }}>
                Something went wrong
              </h2>
              <p style={{ color: "#999", marginBottom: "1.5rem", lineHeight: 1.5 }}>
                {error?.message || "An unexpected error occurred."}
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
                <button
                  onClick={reset}
                  style={{
                    padding: "0.6rem 1.5rem",
                    background: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    padding: "0.6rem 1.5rem",
                    background: "#22c55e",
                    color: "#000",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Reload Page
                </button>
              </div>
            </>
          )}
        </div>
      </body>
    </html>
  );
}
